import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  type WheelEvent
} from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';
import moment from 'moment';
import Path from 'path-to-regexp';
import { NavLink } from 'react-router-dom';

import { cn } from '@/shadcn/lib/utils';
import { Skeleton } from '@/shadcn/skeleton';

import { ROUTE_INSPECT_TRACE_LINK } from '@/constant/routes';
import { Segment } from '../segment/segment';
import type { LinkQueryResult } from '../traceInspector.types';
import { isMigrationLink } from '../traceInspector.utils';
import {
  computeFetchWindow,
  heightToIndex,
  RIBBON_PAGE_SIZE
} from './hooks/ribbonWindow';

type Props = {
  activeSegment: LinkQueryResult | undefined;
  pulldown: ReactNode;
  links: LinkQueryResult[];
  traceId: string;
  /** Reveal technical migration links, which are hidden for everyone by default. */
  showMigrations?: boolean;
  // Load an offset-anchored page (see useTraceLinksPagination). Fired whenever
  // the visible index range (plus overscan) contains unloaded links.
  onLoadRange: (offset: number, first: number) => void;
  loading: boolean;
  // Total links in the trace. This is the virtualizer's item count and, because
  // heights are contiguous 1..totalCount, it equals the head height — so every
  // link's absolute index is simply height - 1 (see ribbonWindow.ts).
  totalCount: number;
};

type NumberOfAnswersByLinks = Record<string, number>;

// Nominal tile width (px). The virtualizer measures the real rendered width of
// each tile via measureElement, so this is only the pre-measurement estimate.
const SEGMENT_WIDTH = 161;
// Height of the ribbon row (one tile + its bottom margin). The tiles are
// absolutely positioned, so the scroll content needs an explicit height.
const RIBBON_ROW_HEIGHT = 216;
// Extra indices loaded on each side of the visible range so a page is already
// in flight before an unloaded tile scrolls into view. Exported for the tests
// that assert the exact fetch window.
export const RIBBON_OVERSCAN = 8;

const getNumberOfAnswersByLinks = (
  linksToCheck: LinkQueryResult[]
): NumberOfAnswersByLinks =>
  linksToCheck.reduce((answerCounts, link) => {
    const { data } = link;
    if (
      data &&
      'commentLinkHash' in data &&
      typeof data.commentLinkHash === 'string'
    ) {
      answerCounts[data.commentLinkHash] =
        (answerCounts[data.commentLinkHash] || 0) + 1;
    }
    return answerCounts;
  }, {});

const getTimeInterval = (date1: string, date2: string): string => {
  const time2 = moment.utc(date2).local();
  const time1 = moment.utc(date1).local();
  return moment.duration(time2.diff(time1)).humanize();
};

// Migration links are hidden the same way answerComment links are: they keep
// their index so the index↔height mapping the virtualizer depends on stays exact,
// and render as a zero-width tile. Dropping them from `links` instead would
// misalign every tile after the first migration.
const isHidden = (link: LinkQueryResult, showMigrations: boolean): boolean =>
  link.actionKey === 'answerComment' ||
  (!showMigrations && isMigrationLink(link));

// A placeholder tile shown at an index whose link hasn't loaded yet. The
// visible range's skeletons are what tell computeFetchWindow which offset page
// to pull, so their mere presence drives pagination.
// Mirrors the real Segment's vertical rhythm: header (height number + action
// name), the action icon, then footer (date + group name + avatar).
const SkeletonTile = () => (
  <div className="flex w-[161px] flex-col items-center gap-1.5 pt-[26px]">
    <Skeleton className="h-[9px] w-[24px]" />
    <Skeleton className="h-[11px] w-[90px]" />
    <Skeleton className="mt-2 h-9 w-11" />
    <Skeleton className="mt-2 h-[10px] w-[46px]" />
    <Skeleton className="h-[11px] w-[80px]" />
    <Skeleton className="mt-1 h-9 w-9 rounded-full" />
  </div>
);

/**
 * Index-virtualized segment ribbon.
 *
 * The whole trace is virtualized by ABSOLUTE INDEX (0 = oldest link on the
 * left, totalCount-1 = head on the right). @tanstack/react-virtual renders only
 * the visible window; loaded links map to their index by `height - 1`, and any
 * index without a loaded link renders a skeleton. The visible range maps
 * straight to a single offset fetch (computeFetchWindow → onLoadRange), and the
 * Trace.links cache policy unions every page — so a hard scroll / scrollbar jump
 * to a far region is a first-class operation, not a special re-anchor case.
 */
export const SegmentList = ({
  activeSegment,
  pulldown,
  links = [],
  traceId,
  onLoadRange,
  loading,
  totalCount,
  showMigrations = false
}: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Fire the mount centering scroll exactly once, even if activeSegment /
  // totalCount only become resolvable on a later render.
  const didCenter = useRef(false);

  const count = totalCount > 0 ? totalCount : 0;

  const virtualizer = useVirtualizer({
    count,
    horizontal: true,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => SEGMENT_WIDTH,
    overscan: RIBBON_OVERSCAN
  });

  // Index → loaded link. Memoized on `links` so the fetch effect's dependency
  // is referentially stable across renders where the loaded window is unchanged.
  const linksByIndex = useMemo(() => {
    const map = new Map<number, LinkQueryResult>();
    for (const link of links) {
      map.set(heightToIndex(link.height), link);
    }
    return map;
  }, [links]);

  const numberOfAnswersByLinks = useMemo(
    () => getNumberOfAnswersByLinks(links),
    [links]
  );

  const virtualItems = virtualizer.getVirtualItems();
  const startIndex = virtualItems[0]?.index;
  const endIndex = virtualItems.at(-1)?.index;

  // Whenever the buffered range holds an unloaded index, pull the single
  // offset-anchored page that covers it. The pagination hook coalesces the
  // burst a fast scroll produces (in-flight lock) and the `loading` gate keeps
  // us from stacking fetches; once a region loads, computeFetchWindow returns
  // null for it so this settles.
  useEffect(() => {
    if (loading) {
      return;
    }
    if (startIndex == null || endIndex == null) {
      return;
    }
    const window = computeFetchWindow({
      startIndex,
      endIndex,
      isLoaded: index => linksByIndex.has(index),
      totalCount: count,
      overscan: RIBBON_OVERSCAN,
      pageSize: RIBBON_PAGE_SIZE
    });
    if (window) {
      onLoadRange(window.offset, window.first);
    }
  }, [startIndex, endIndex, count, linksByIndex, loading, onLoadRange]);

  // Center the active segment on mount. scrollToIndex positions by absolute
  // index, so there's no DOM measurement / compensation dance — the virtualizer
  // scrolls straight to the target even if its tile isn't loaded yet.
  const activeIndex =
    activeSegment?.height != null ? heightToIndex(activeSegment.height) : null;
  useEffect(() => {
    if (didCenter.current) {
      return;
    }
    if (activeIndex == null || count <= 0) {
      return;
    }
    didCenter.current = true;
    virtualizer.scrollToIndex(activeIndex, { align: 'center' });
  }, [activeIndex, count, virtualizer]);

  // Translate vertical wheel movement into horizontal scroll so a mouse wheel
  // browses the timeline.
  const handleWheelEvent = (event: WheelEvent<HTMLDivElement>): void => {
    if (!scrollRef.current) {
      return;
    }
    scrollRef.current.scrollLeft += event.deltaY;
  };

  const totalSize = virtualizer.getTotalSize();

  const renderVirtualItem = (item: {
    index: number;
    start: number;
    size: number;
  }): ReactNode => {
    const { index } = item;
    const baseStyle: CSSProperties = {
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      transform: `translateX(${item.start}px)`
    };

    const link = linksByIndex.get(index);

    if (!link) {
      return (
        <div
          data-index={index}
          data-testid="ribbon-skeleton"
          key={index}
          ref={virtualizer.measureElement}
          style={baseStyle}
        >
          <SkeletonTile />
        </div>
      );
    }

    // Hidden links (answerComment, and migrations unless revealed) occupy a chain
    // height but aren't shown. They still hold an index so the index↔height
    // mapping stays exact — rendered as a zero-width tile the virtualizer
    // measures to 0.
    if (isHidden(link, showMigrations)) {
      return (
        <div
          data-index={index}
          data-testid="ribbon-hidden"
          key={index}
          ref={virtualizer.measureElement}
          style={{ ...baseStyle, width: 0, overflow: 'hidden' }}
        />
      );
    }

    const isFirst = index === 0;
    const isLast = index === count - 1;
    const isActive = activeSegment?.linkHash === link.linkHash;
    const prev = linksByIndex.get(index - 1);
    const interval =
      prev && !isHidden(prev, showMigrations)
        ? getTimeInterval(prev.createdAt, link.createdAt)
        : null;

    return (
      <NavLink
        className="relative z-1 flex shrink-0 cursor-pointer justify-center no-underline active:cursor-default"
        data-index={index}
        data-link-hash={link.linkHash}
        draggable={false}
        key={index}
        ref={virtualizer.measureElement}
        style={baseStyle}
        to={Path.compile(ROUTE_INSPECT_TRACE_LINK)({
          id: traceId,
          linkid: link.linkHash
        })}
      >
        <Segment
          answersCount={numberOfAnswersByLinks[link.linkHash]}
          interval={interval}
          isActive={isActive}
          isFirst={isFirst}
          isLast={isLast}
          isMigration={isMigrationLink(link)}
          link={link}
        />
      </NavLink>
    );
  };

  return (
    <div
      className={cn(
        'relative w-full border-border border-b bg-card',
        'before:absolute before:top-0 before:left-0 before:z-2 before:h-[90%] before:w-[70px] before:content-[""]',
        'before:bg-linear-to-r before:from-card before:to-transparent'
      )}
    >
      <div
        className="scrollbar-thin relative z-1 box-border overflow-auto overscroll-contain px-5 pt-subnavbar"
        onWheel={handleWheelEvent}
        ref={scrollRef}
      >
        <div
          style={{
            position: 'relative',
            width: `${totalSize}px`,
            height: `${RIBBON_ROW_HEIGHT}px`
          }}
        >
          {virtualItems.map(renderVirtualItem)}
          {/* The next-action pulldown sits just past the head (rightmost) tile
              and scrolls with the ribbon, so it's absolutely positioned at the
              end of the virtualized content. */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              transform: `translateX(${totalSize}px)`
            }}
          >
            {pulldown}
          </div>
        </div>
      </div>
    </div>
  );
};
